<template>
  <Handle
    v-if="!isActiveTemplatePublished"
    type="target"
    :position="Position.Top"
    :connectable="true"
    :connectable-start="false"
    :connectable-end="!isFirstStep"
    class="cursor-pointer! h-2! w-2!"
    :class="[
      { 'opacity-0': isFirstStep },
      isNodeSelected
        ? 'bg-blue-500! border-blue-500!'
        : stepHighlightedForError
          ? 'bg-red-500! border-red-500!'
          : undefined,
    ]"
    :style="{
      backgroundColor: serviceColor,
      borderColor: serviceColor,
    }"
  />
  <div
    class="service-block z-10 shadow-sm"
    :class="[
      stepHighlightedForError
        ? 'border border-red-500 bg-red-50'
        : 'border border-transparent bg-white',
      {
        'border-replacing border-2 border-dashed border-blue-400':
          replacingCurrentStep,
        'service-block-border':
          !replacingCurrentStep && !showReplaceDropZone && !stepIsUnusable,
        'drag-over-service': showReplaceDropZone,
        'is-selected': isSelected,
        'border-selected':
          nodeCtx?.node?.selected ||
          (activeStep?.id === step?.id &&
            activePanel &&
            activePanel !== PANEL_TYPE_CONDITIONAL),
      },
    ]"
    @click.exact="handleStepClick"
    @drop="handleDataServiceDrop($event)"
    @dragenter="handleDragEnter"
    @dragleave="intersectingServiceBlock = false"
    @dragover.prevent
  >
    <replace-service-dialog
      v-if="replacingCurrentStep"
      :replace-from="replacingStepTitle"
      :replace-to="replacementStepTitle"
    />

    <p
      v-else-if="showReplaceDropZone"
      class="pointer-events-none h-full w-full space-x-1 px-6 py-8 text-left text-base break-normal whitespace-pre-line text-gray-500"
      @dragover.prevent
    >
      <span>{{ $t("COMMON.REPLACE") }}</span>
      <span class="font-bold">{{ step?.title ?? "" }}</span>
      <span>{{ $t("COMMON.WITH") }}</span>
      <span class="font-bold">{{ draggable?.serviceData?.title ?? "" }}</span>
    </p>

    <unusable-step-dialog v-else-if="stepIsUnusable && step" :step="step" />

    <div
      v-else
      class="flex w-full flex-col items-start space-y-3 overflow-hidden"
    >
      <div
        v-if="step?.label"
        class="flex w-full items-center space-x-2 border-b px-4 py-2"
      >
        <lf-checkbox
          v-if="selectMode"
          v-model="isSelected"
          name="checkStep"
          :value="step?.id"
          @change="selectStep"
        />
        <span
          class="truncate font-semibold text-gray-700"
          data-cy="do-step-label"
        >
          {{ step.label }}
        </span>
      </div>

      <div
        class="flex w-full items-center space-x-4 px-4"
        :class="step?.label ? 'py-1' : 'border-b py-2'"
      >
        <div class="flex space-x-2 truncate">
          <lf-checkbox
            v-if="selectMode && !step?.label"
            v-model="isSelected"
            name="checkStep"
            :value="step?.id"
            @change="selectStep"
          />
          <div class="my-1.5 flex items-center space-x-2 overflow-hidden">
            <icon-base
              v-if="step?.title === SERVICE_CUSTOM_ATTRIBUTES"
              :icon="IconFormulaView"
              class="text-primary"
            />
            <img
              v-else-if="step?.title === SERVICE_APPLICATION_DATA"
              :src="getImageForService(SERVICE_APPLICATION_DATA) || undefined"
              :alt="SERVICE_APPLICATION_DATA"
              class="h-5 w-5 shrink-0"
              loading="lazy"
            />
            <template v-else-if="stepDetailsData.serviceData?.id">
              <img
                :src="
                  getImageForService(stepDetailsData.serviceData.id) ||
                  undefined
                "
                class="h-4 w-4 shrink-0"
                loading="lazy"
              />
              <span
                class="font-semibold text-gray-700"
                data-cy="do-step-service-name"
              >
                {{ stepDetailsData.serviceData.name }}
              </span>
              <icon-base
                :icon="IconCircle"
                width="8"
                height="8"
                view-box="0 0 8 8"
                :icon-color="serviceColor"
              />
            </template>
            <span
              class="truncate text-gray-700"
              :class="step?.label ? 'text-sm' : 'font-medium'"
              data-cy="do-step-title"
            >
              {{ serviceDisplayText }}
            </span>
          </div>
        </div>
      </div>
      <div
        v-if="stepDetailsData.attributesNames"
        class="flex w-full flex-col px-4"
      >
        <div
          v-if="stepDetailsData.vendorName"
          class="flex w-full items-center space-x-1 text-xs font-medium"
        >
          <span class="text-gray-400 uppercase">
            {{ $t("ORCHESTRATION.DATA_SECTION") }}:
          </span>
          <span
            v-tooltip="{
              content: stepDetailsData.vendorName,
              placement: 'bottom',
            }"
            class="truncate text-gray-600"
            data-cy="do-step-vendor-name"
          >
            {{ stepDetailsData.vendorName }}
          </span>
        </div>
        <div class="flex w-full items-center space-x-1 text-xs font-medium">
          <span class="text-gray-400 uppercase">
            {{ $t("ORCHESTRATION.ATTRIBUTES") }}:
          </span>
          <span
            v-tooltip="{
              content: stepDetailsData.attributesNames,
              placement: 'bottom',
            }"
            class="truncate text-gray-600"
            data-cy="do-step-attributes-names"
          >
            {{ stepDetailsData.attributesNames }}
          </span>
        </div>
      </div>
      <div
        v-else
        class="flex w-full justify-center space-x-2 pt-2.5 text-blue-700"
      >
        <icon-base
          width="16"
          height="16"
          view-box="0 0 5 12"
          :icon="IconSourceBlue"
        />
        <span>{{ $t("ORCHESTRATION.SET_ATTRIBUTES") }}</span>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, defineAsyncComponent, ref, watch } from "vue";
import { useStore } from "vuex";

import { Handle, Position, useNode } from "@vue-flow/core";
import compact from "lodash/compact";

import {
  DO_MANUAL_REVIEW,
  DO_TYPE_DATA_SERVICE,
  PANEL_TYPE_CONDITIONAL,
  SERVICE_APPLICATION_DATA,
  SERVICE_CUSTOM_ATTRIBUTES,
} from "@/helpers/constants";
import {
  STEP_NODE_HEIGHT,
  STEP_NODE_HEIGHT_WITH_LABEL,
  STEP_NODE_WIDTH,
} from "@/helpers/constants/orchestration";
import { getAttributeId } from "@/helpers/customAttributes";
import { wordsFirstLetterToUpper } from "@/helpers/formatting";
import { getServiceColor, getStepSidebarType } from "@/helpers/orchestration";
import { getImageForService } from "@/helpers/services";
import {
  useAttributesOptions,
  useAttributesOptionsMap,
  useDataOrchestration,
  useDoActiveTemplateStatus,
} from "@/hooks/orchestration";

import IconCircle from "@/components/icons/IconCircle.vue";
import IconSourceBlue from "@/components/icons/IconSourceBlue.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";
import ReplaceServiceDialog from "@/views/profile/components/dataOrchestration/ReplaceServiceDialog.vue";
import UnusableStepDialog from "@/views/profile/components/dataOrchestration/UnusableStepDialog.vue";

import type {
  ActiveConfigPanel,
  IAttribute,
  IDataOrchestrationFlowOutcome,
  IDataService,
  IDataServiceService,
  IDoDraggable,
  IStep,
} from "@/models/orchestration";

const IconFormulaView = defineAsyncComponent(
  () => import("@/components/icons/IconFormulaView.vue")
);

const stepWidth = `${STEP_NODE_WIDTH}px`;

const emit = defineEmits<{
  "intersecting:start": [];
}>();

const props = defineProps<{
  draggable?: IDoDraggable;
  selectMode?: boolean;
}>();

const { getters, commit } = useStore();
const { attributeOptions } = useAttributesOptions();
const { allAttributes } = useAttributesOptionsMap();
const { activeStep, activeTemplateErrors } = useDataOrchestration();
const nodeCtx = useNode<{ step?: IStep }>();
const { isActiveTemplatePublished } = useDoActiveTemplateStatus();

const step = computed(() => nodeCtx.node?.data?.step);

const stepHeight = computed(() =>
  step.value?.label
    ? `${STEP_NODE_HEIGHT_WITH_LABEL}px`
    : `${STEP_NODE_HEIGHT}px`
);

const isNodeSelected = computed(() => nodeCtx.node.selected);

const stepHighlightedForError = computed(() => {
  if (
    !step.value?.id ||
    replacingCurrentStep.value ||
    showReplaceDropZone.value
  ) {
    return false;
  }
  const errorCtx = activeTemplateErrors.value[step.value.id];
  return !!errorCtx?.errors.length;
});

const intersectingServiceBlock = ref(false);
const isSelected = ref(false);
const isActiveStep = ref(false);

const selectedSteps = computed<string[]>(
  () => getters["orchestration/selectedSteps"]
);

const replacementStep = computed<IDataService>(
  () => getters["orchestration/replacementStep"]
);

const replaceStepId = computed<string>(
  () => getters["orchestration/replaceStepId"]
);

const replacementStepTitle = computed(() => {
  const replaceStepServiceInfo: IDataService | undefined = getters[
    "orchestration/serviceInfoByTitle"
  ](replacementStep.value?.title);

  const replaceStepProvider = replaceStepServiceInfo?.services?.find(
    ({ id }) => id === replacementStep.value?.provider
  );
  return [replaceStepProvider?.name, replacementStep.value?.title]
    .filter(Boolean)
    .join(" · ");
});

const copiedSteps = computed<IStep[]>(
  () => getters["orchestration/copiedSteps"]
);

const activeTemplateSteps = computed<IStep[]>(
  () => getters["orchestration/activeTemplateSteps"]
);

const serviceInfo = computed<IDataService | undefined>(() =>
  getters["orchestration/serviceInfoByTitle"](step.value?.title)
);

const activePanel = computed<ActiveConfigPanel>(
  () => getters["orchestration/activeConfigPanel"]
);

const isFirstStep = computed(
  () => activeTemplateSteps.value[0]?.id === step.value?.id
);

const stepIsUnusable = computed(() => {
  return (
    !step.value ||
    (!step.value.provider &&
      !step.value.attributes?.length &&
      !(
        [SERVICE_APPLICATION_DATA, SERVICE_CUSTOM_ATTRIBUTES] as string[]
      ).includes(step.value.title as string))
  );
});

const replacingCurrentStep = computed(
  () => replaceStepId.value === step.value?.id && !!replacementStep.value
);
const replacingStepTitle = computed(() => {
  const provider = serviceInfo.value?.services?.find(
    ({ id }) => id === step.value?.provider
  );
  return [provider?.name, step.value?.title].filter(Boolean).join(" · ");
});

const stepType = computed(() => getStepSidebarType(step.value));

const stepAttributesData = computed<IAttribute[]>(() =>
  compact(
    step.value?.attributes?.map((attribute) =>
      attributeOptions.value.find(
        (option) => getAttributeId(option) === getAttributeId(attribute)
      )
    )
  )
);

const stepDetailsData = computed<{
  attributesNames: string;
  vendorName: string;
  serviceData: IDataServiceService | null;
}>(() => {
  if (!step.value) {
    return {
      attributesNames: "",
      vendorName: "",
      serviceData: null,
    };
  }
  if (!stepAttributesData.value.length) {
    const provider = serviceInfo.value?.services?.find(
      ({ id }) => id === step.value?.provider
    );
    return {
      attributesNames: "",
      vendorName: "",
      serviceData: provider ?? null,
    };
  }

  const attributesNames: string = stepAttributesData.value
    .map((attr) => attr?.name || wordsFirstLetterToUpper(attr?.id, "_"))
    .join(", ");

  if (!serviceInfo.value?.data_service_details) {
    return {
      attributesNames,
      vendorName: "",
      serviceData: null,
    };
  }

  const serviceName: string =
    allAttributes.value[stepAttributesData.value[0]?.id]?.service || "";

  const serviceData =
    serviceInfo.value?.services?.find(
      (service) => service.name === serviceName
    ) || null;

  const vendorKey: string = [
    ...new Set(stepAttributesData.value.map((attr) => attr.service_id)),
  ]?.join("");

  const vendorsData = Object.values(serviceInfo.value.data_service_details)
    .flatMap((obj) => Object.entries(obj))
    .filter((vendor: [string, IAttribute[]]) => {
      if (serviceData?.name.toLowerCase().includes("transunion")) {
        return vendor[1].some(({ id }) =>
          activeTemplateSteps.value.some((step) =>
            step.attributes?.some((attr) => attr.id === id)
          )
        );
      }
      return vendor[1][0].service_id === vendorKey;
    });

  const vendorName = vendorsData.length
    ? vendorsData.map((vendor) => vendor[0]).join(", ")
    : "";

  return {
    attributesNames,
    vendorName,
    serviceData,
  };
});

const serviceColor = computed(() => getServiceColor(step.value?.title || null));

const serviceDisplayText = computed(() => {
  return step.value?.title || step.value?.id || "";
});

const showDataServiceDropZone = computed(
  () =>
    props.draggable?.type === DO_TYPE_DATA_SERVICE &&
    props.draggable?.isDragging
);

const showReplaceDropZone = computed(
  () => showDataServiceDropZone.value && intersectingServiceBlock.value
);

const handleDataServiceDrop = (event: DragEvent) => {
  if (!intersectingServiceBlock.value) {
    return;
  }
  intersectingServiceBlock.value = false;
  const jsonData = event.dataTransfer?.getData("text");
  if (!jsonData) {
    return;
  }
  const data = JSON.parse(jsonData);
  commit("orchestration/setReplaceStepId", step.value?.id);
  commit("orchestration/setReplacementStep", data);
};

const handleDragEnter = () => {
  intersectingServiceBlock.value = true;
  emit("intersecting:start");
};

const showPasteButton = (outcome: IDataOrchestrationFlowOutcome) => {
  if (!copiedSteps.value.length || outcome.outcome === DO_MANUAL_REVIEW) {
    return false;
  }

  const templateStep = activeTemplateSteps.value.find(
    ({ id }) => id === step.value?.id
  );

  if (!templateStep) {
    return false;
  }

  if (outcome.next_step) {
    return false;
  }

  return true;
};

const selectStep = () => {
  commit("orchestration/changeSelectedSteps", step.value?.id);
};

const handleStepClick = () => {
  if (replaceStepId.value || stepIsUnusable.value) {
    return;
  }

  if (selectedSteps.value.length) {
    isActiveStep.value = true;
  }

  commit("orchestration/setActiveConfigPanel", stepType.value);
  commit("orchestration/setActiveStep", step.value);
};

watch(
  selectedSteps,
  (steps) => {
    isSelected.value = !!step.value?.id && steps.includes(step.value.id);
  },
  { deep: true }
);

watch(
  step,
  (updatedStep) => {
    updatedStep?.outcomes.forEach((outcome) => showPasteButton(outcome));
  },
  { deep: true }
);
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.service-block {
  height: v-bind(stepHeight);
  width: v-bind(stepWidth);
  max-height: v-bind(stepHeight);
  min-height: v-bind(stepHeight);
  border-radius: 4px;
  cursor: pointer;
  overflow: hidden;

  &:hover {
    background: #f9fafb;
  }
}

.service-block.is-selected {
  background: #eff6ff;
  border-color: #60a5fa;
  border-width: 2px;
}

.service-block.is-selected:hover {
  background: #bfdbfe;
}

.service-block-border {
  border-left: 4px solid !important;
  border-left-color: v-bind(serviceColor) !important;
}
.border-replacing {
  border: 2px dashed !important;
  border-color: #3869d6 !important;
}
.border-selected {
  border: 1px solid #3869d6 !important;
}
.drag-over-service {
  border: 2px dashed !important;
  border-color: #c9cdd2 !important;
  background-color: #f9f9fb !important;
  box-shadow: none;
}
</style>
